import { FC, memo, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { GridRow, GridRowProps } from "@mui/x-data-grid";
import { DataTable, useDataTableState } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { SearchToggle } from "@/components/ui/SearchToggle";
import { ConfirmationDialog } from "@/components/Dialog/ConfirmationDialog";
import { ExportQuoteDialog } from "@/components/ExportQuote/ExportQuoteDialog";
import { useSnackbar } from "@/components/Snackbar";
import { useDialog } from "@/hooks/useDialog";
import { useDebounce } from "@/hooks/useDebounce";
import {
  deleteExportQuote,
  fetchExportQuotes,
} from "@/apis/bacchus/exportQuote";
import {
  ExportQuote,
  ExportQuoteListResponse,
  SegmentFilterBy,
} from "@/apis/bacchus/types";
import { SegmentPageLayout } from "../Segments/components/SegmentPageLayout";
import { SegmentsToolbar } from "../Segments/SegmentsList/SegmentsToolbar";
import { getColumns } from "./columns";

// Row slot that adds a stable data-testid per quote row for e2e targeting
const QuoteRow = (props: GridRowProps) => (
  <GridRow {...props} data-testid={`quote-row-${props.rowId}`} />
);

const QuotesSearchEmptyState: FC = memo(function QuotesSearchEmptyState() {
  return (
    <EmptyState
      icon={SearchOffIcon}
      title="No quotes found"
      description="No quotes match your search. Try a different search term."
    />
  );
});

const QuotesEmptyState: FC = memo(function QuotesEmptyState() {
  return (
    <EmptyState
      icon={RequestQuoteOutlinedIcon}
      title="No quotes yet"
      description="Run Count in the export dialog of the Companies page, then Save Quote to keep its filters, settings and estimate."
    />
  );
});

const QuotesErrorState: FC = memo(function QuotesErrorState() {
  return (
    <EmptyState
      icon={ErrorOutlineIcon}
      title="Unable to load quotes"
      description="Something went wrong while fetching your quotes. Please try again or contact support if the problem persists."
      variant="error"
    />
  );
});

export const QuotesListPage: FC = () => {
  const tableState = useDataTableState({ initialPageSize: 50 });

  const [filterBy, setFilterBy] = useState<SegmentFilterBy>("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 700);

  const [data, setData] = useState<ExportQuoteListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Preserve rowCount during loading to maintain pagination stability
  const [lastRowCount, setLastRowCount] = useState<number | undefined>();

  const deleteDialog = useDialog<ExportQuote>();
  const editDialog = useDialog<ExportQuote>();
  const [isDeleting, setIsDeleting] = useState(false);

  const { showSuccess, showError } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    tableState.resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy, debouncedSearch]);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // API uses 1-indexed pages, DataGrid uses 0-indexed
      const result = await fetchExportQuotes({
        page: tableState.paginationModel.page + 1,
        pageSize: tableState.paginationModel.pageSize,
        filterBy,
        search: debouncedSearch || undefined,
      });

      setData(result);
      if (result.meta?.total_count !== undefined) {
        setLastRowCount(result.meta.total_count);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch quotes")
      );
    } finally {
      setLoading(false);
    }
  }, [
    tableState.paginationModel.page,
    tableState.paginationModel.pageSize,
    filterBy,
    debouncedSearch,
  ]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialog.data?.id) return;

    setIsDeleting(true);
    try {
      await deleteExportQuote(deleteDialog.data.id);
      deleteDialog.close();
      showSuccess("Quote deleted successfully");
      loadQuotes();
    } catch (err) {
      console.error("Failed to delete quote:", err);
      showError("Failed to delete quote. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteDialog, showSuccess, showError, loadQuotes]);

  const handleEditSave = useCallback(() => {
    editDialog.close();
    loadQuotes();
  }, [editDialog, loadQuotes]);

  // Opens the Companies page with the quote's filters and its export settings
  const handleRowClick = useCallback(
    (row: ExportQuote) => navigate(`/segments/explore?quote=${row.id}`),
    [navigate]
  );

  const columns = useMemo(
    () => getColumns(deleteDialog.open, editDialog.open),
    [deleteDialog.open, editDialog.open]
  );

  // Empty during loading to prevent old data showing beneath skeletons
  const rows = useMemo(
    () => (loading ? [] : data?.data || []),
    [data?.data, loading]
  );

  const rowCount = loading ? lastRowCount : (data?.meta?.total_count ?? 0);

  return (
    <SegmentPageLayout
      title="Quotes"
      action={
        <Box data-testid="quotes-search-input">
          <SearchToggle
            searchTerm={search}
            onSearchChange={setSearch}
            placeholder="Search quotes..."
            width={250}
          />
        </Box>
      }
    >
      <SegmentsToolbar
        filterBy={filterBy}
        onFilterByChange={setFilterBy}
        testIdPrefix="quotes"
      />

      <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Box data-testid="quotes-table" sx={{ height: "100%" }}>
            <DataTable<ExportQuote>
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              rowCount={rowCount ?? 0}
              paginationModel={tableState.paginationModel}
              onPaginationModelChange={tableState.onPaginationModelChange}
              loading={loading}
              error={error ?? undefined}
              onRowClick={handleRowClick}
              slots={{
                row: QuoteRow,
                empty: debouncedSearch
                  ? QuotesSearchEmptyState
                  : QuotesEmptyState,
                error: QuotesErrorState,
              }}
            />
          </Box>
        </Box>
      </Box>

      <ConfirmationDialog
        open={deleteDialog.isOpen}
        title="Delete Quote"
        description={`Are you sure you want to delete "${deleteDialog.data?.name ?? "this quote"}"? This action cannot be undone.`}
        cancelButtonText="Cancel"
        okayButtonText={
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            data-testid="quote-delete-confirm-btn"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        }
        onClose={deleteDialog.close}
      />

      <ExportQuoteDialog
        open={editDialog.isOpen}
        mode="edit"
        quote={editDialog.data}
        onClose={editDialog.close}
        onSave={handleEditSave}
      />
    </SegmentPageLayout>
  );
};
